import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { orgSelector } from 'src/redux/selectors';
import { AnalyticsEvents } from './events';
import { isAwaitingSensitiveParamStrip, sanitizeSearchForAnalytics } from './sanitizeSearch';
import {
   clearOrganizationContext,
   getDistinctId,
   identifyUser,
   initAnalytics,
   isAnalyticsEnabled,
   resetUser,
   setOrganizationContext,
   setOrganizationGroup,
   track as rawTrack,
   type IdentifyPayload,
   type TrackOptions,
} from './mixpanel';

interface AnalyticsContextValue {
   track: (event: string, properties?: Record<string, unknown>, options?: TrackOptions) => void;
   identify: (payload: IdentifyPayload) => void;
   reset: () => void;
   setOrganization: (organizationId: string) => void;
   getDistinctId: () => string | null;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export const MixpanelProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
   // Initialize the SDK exactly once, on mount. Safe to call multiple times —
   // the wrapper guards against double-init.
   useEffect(() => {
      initAnalytics();
   }, []);

   const location = useLocation();
   const params = useParams();
   const prevPathRef = useRef<string | null>(null);

   // Track the currently active organization (nonprofit). This is set by
   // landing/org-microsite pages for anonymous visitors and by AuthContext
   // after login. Pushing it into Mixpanel as a super-property means every
   // downstream event is sliceable by org name/slug without extra wiring.
   const activeOrg = useSelector((state: any) => orgSelector(state)?.organization);
   useEffect(() => {
      if (!isAnalyticsEnabled()) return;
      if (activeOrg?.id) {
         setOrganizationContext({
            id: activeOrg.id,
            slug: activeOrg.slug,
            name: activeOrg.name,
         });
      } else {
         clearOrganizationContext();
      }
   }, [activeOrg?.id, activeOrg?.slug, activeOrg?.name]);

   // Auto-emit page_view on every route change. Mixpanel has its own
   // track_pageview, but we want our route name + org context attached.
   useEffect(() => {
      if (!isAnalyticsEnabled()) return;
      const path = location.pathname;
      if (prevPathRef.current === path) return;
      // Re-runs on the search change once the page has replaced its URL without the token.
      if (isAwaitingSensitiveParamStrip(path, location.search)) return;
      prevPathRef.current = path;
      rawTrack(AnalyticsEvents.PAGE_VIEW, {
         path,
         search: sanitizeSearchForAnalytics(location.search),
         organizationSlug: params.organizationSlug,
         hotelId: params.hotelId,
      });
   }, [location.pathname, location.search, params.organizationSlug, params.hotelId]);

   const value = useMemo<AnalyticsContextValue>(
      () => ({
         track: rawTrack,
         identify: identifyUser,
         reset: resetUser,
         setOrganization: setOrganizationGroup,
         getDistinctId,
      }),
      [],
   );

   return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
};

/**
 * Hook for emitting analytics events from components.
 *
 * Safe to call even when the provider is absent — falls back to no-ops so
 * components can be rendered in tests or in contexts without analytics.
 */
// eslint-disable-next-line react-refresh/only-export-components -- co-located with its provider; splitting would ripple through every import site
export const useAnalytics = (): AnalyticsContextValue => {
   const ctx = useContext(AnalyticsContext);
   if (ctx) return ctx;
   return {
      track: () => undefined,
      identify: () => undefined,
      reset: () => undefined,
      setOrganization: () => undefined,
      getDistinctId: () => null,
   };
};
